import { useCallback, useMemo } from 'react'

import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { fingerprint, successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import {
	fetchAvailableIntegrations,
	fetchEventSchema,
	fetchIntegrationDefinition,
	fetchIntegrationDetails,
	fetchIntegrationEventTypes,
	fetchListedSources,
	fetchTenantIntegrations,
	fetchTenantsIntegrations,
	getMicrosoftConsentURL,
} from './api'
import { enrichIntegrations } from './enrich'

import type {
	AvailableIntegration,
	EnrichedIntegration,
	Integration,
	IntegrationConnector,
} from './types'
import type { QueryClient, UseQueryResult } from '@tanstack/react-query'

export const integrationKeys = {
	all: ['integrations'] as const,

	availableList: () => [...integrationKeys.all, 'available'] as const,
	available: (tenant: string) =>
		[...integrationKeys.availableList(), tenant] as const,

	lists: () => [...integrationKeys.all, 'list'] as const,
	list: (tenant: string) => [...integrationKeys.lists(), tenant] as const,
	// Several tenants' connectors, from `GET /tenants/all/connectors`. Under
	// `lists()`, so `invalidateIntegrations` refreshes it with the one-tenant
	// list. `tenants` is fingerprinted, as in `multiTenantKeys`.
	tenantsList: (tenants: ReadonlyArray<string>) =>
		[
			...integrationKeys.lists(),
			'all',
			{ tenants: fingerprint(tenants) },
		] as const,
	listEvents: (tenant: string) =>
		[...integrationKeys.list(tenant), 'with-events'] as const,

	details: () => [...integrationKeys.all, 'details'] as const,
	detail: (tenant: string, integration_id: string) =>
		[...integrationKeys.details(), tenant, integration_id] as const,

	eventsTypes: () => [...integrationKeys.details(), 'event-types'] as const,
	eventTypes: (tenant: string, integration_id: string) =>
		[...integrationKeys.eventsTypes(), tenant, integration_id] as const,

	eventSchemas: () => [...integrationKeys.details(), 'event-schema'] as const,
	eventSchema: (tenant: string, definition_id: string, event_type: string) =>
		[
			...integrationKeys.eventSchemas(),
			tenant,
			definition_id,
			event_type,
		] as const,

	definitions: () => [...integrationKeys.all, 'definitions'] as const,
	definition: (tenant: string, integration_id: string) =>
		[...integrationKeys.definitions(), tenant, integration_id] as const,

	// Not under `lists()`: Collector writes change it too, so no Integrations
	// invalidation could keep it fresh; its query is always stale instead.
	listedSources: (tenant: string) =>
		[...integrationKeys.all, 'listed-sources', tenant] as const,

	microsoftConsentURL: (tenant: string) =>
		[...integrationKeys.all, 'microsoft-consent-url', tenant] as const,
}

/**
 * Helper function to invalidate integration-related queries sequentially
 * @param queryClient - The query client to invalidate the queries on
 */
export async function invalidateIntegrations(queryClient: QueryClient) {
	// The enriched list is derived (a `useMemo` join over these two queries), so
	// invalidating the catalog + tenant list is enough for it to re-derive.
	await Promise.all([
		// Invalidate the tenant integrations list
		queryClient.invalidateQueries({
			queryKey: integrationKeys.lists(),
			refetchType: 'all',
		}),
		// Invalidate available integrations list
		queryClient.invalidateQueries({
			queryKey: integrationKeys.availableList(),
			refetchType: 'all',
		}),
	])
}

// Select functions for data transformations (stable references)
const selectAvailableAsMap = (data: AvailableIntegration[]) => {
	const map = new Map<string, AvailableIntegration>()
	for (const integration of data) {
		map.set(integration.id, integration)
	}
	return map
}

const selectEventsAsMap = (data: IntegrationConnector[]) => {
	const map = new Map<string, Integration>()
	for (const { connector } of data) {
		if (connector.events_enabled) {
			map.set(connector.definition_id, connector)
		}
	}
	return map
}

export const integrationQueries = {
	/**
	 * Fetches the list of available integrations from the API and filters out those that are not visible
	 * @param tenant - The tenant ID
	 */
	available: (tenant: string) =>
		queryOptions({
			queryKey: integrationKeys.available(tenant),
			queryFn: ({ signal }) => fetchAvailableIntegrations(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 'm'),
			gcTime: time(1, 'h'),
			select: (data) => data.filter((integration) => integration.visible),
			enabled: !!tenant,
		}),

	/**
	 * List all integrations for the current tenant
	 * @param tenant - The tenant ID
	 */
	list: (tenant: string) =>
		queryOptions({
			queryKey: integrationKeys.list(tenant),
			queryFn: ({ signal }) => fetchTenantIntegrations(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(1, 'm'),
		}),

	/**
	 * List the connectors of several tenants — the multi-tenant tabs' Source
	 * options. One request, whatever the number of tenants.
	 * @param tenants - The tenant IDs
	 */
	tenantsList: (tenants: ReadonlyArray<string>) =>
		queryOptions({
			queryKey: integrationKeys.tenantsList(tenants),
			queryFn: ({ signal }) => fetchTenantsIntegrations(tenants, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(1, 'm'),
		}),

	/**
	 * Get the details for a specific integration
	 * @param tenant - Tenant ID
	 * @param integration_id  - Integration ID
	 */
	detail: (tenant: string, integration_id: string) =>
		queryOptions({
			queryKey: integrationKeys.detail(tenant, integration_id),
			queryFn: ({ signal }) =>
				fetchIntegrationDetails(tenant, integration_id, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 'm'),
			gcTime: time(1, 'h'),
		}),

	/**
	 * Get the definition schema for a specific integration
	 * @param tenant - Tenant ID
	 * @param integration_id  - Integration ID
	 */
	definition: (tenant: string, integration_id: string) =>
		queryOptions({
			queryKey: integrationKeys.definition(tenant, integration_id),
			queryFn: ({ signal }) =>
				fetchIntegrationDefinition(tenant, integration_id, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 'm'),
			gcTime: time(1, 'h'),
		}),

	/**
	 * Get the available event types for a specific integration
	 * @param tenant - Tenant ID
	 * @param integration_id - Integration ID
	 */
	eventTypes: (tenant: string, integration_id: string) =>
		queryOptions({
			queryKey: integrationKeys.eventTypes(tenant, integration_id),
			queryFn: ({ signal }) =>
				fetchIntegrationEventTypes(tenant, integration_id, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 'm'),
			gcTime: time(1, 'h'),
		}),

	/**
	 * Event-schema fetch for a connected integration + event type. Returns
	 * `{ source_schema, normalized_schema? }`. Powers the custom-policy detection
	 * builder, group-by, and metadata pickers.
	 */
	eventSchema: (tenant: string, definition_id: string, event_type: string) =>
		queryOptions({
			queryKey: integrationKeys.eventSchema(tenant, definition_id, event_type),
			queryFn: ({ signal }) =>
				fetchEventSchema(tenant, definition_id, event_type, signal),
			staleTime: ({ state }) => successStaleTime(state, 5, 'h'),
			gcTime: time(1, 'h'),
			enabled: !!tenant && !!definition_id && !!event_type,
		}),

	/**
	 * Every Listed Source the tenant has (`GET logs/sources`), active or not.
	 *
	 * Always stale: connectors (Integrations) and listeners (Collectors) are
	 * written elsewhere and nothing invalidates this key, so the Search
	 * route loader's awaited `prefetchQuery` must refetch on every entry. A
	 * cached list would offer a deleted or disabled source, and default-fill
	 * it into Search's `int`, until it went stale.
	 */
	listedSources: (tenant: string) =>
		queryOptions({
			queryKey: integrationKeys.listedSources(tenant),
			queryFn: ({ signal }) => fetchListedSources(tenant, signal),
			gcTime: time(1, 'h'),
			staleTime: 0,
		}),

	microsoftConsentURL: (tenant: string) =>
		queryOptions({
			queryKey: integrationKeys.microsoftConsentURL(tenant),
			queryFn: ({ signal }) => getMicrosoftConsentURL(tenant, signal),
			staleTime: ({ state }) =>
				successStaleTime(state, state.data?.expires_in ?? 800, 's'), // API returns expires_in with 900s
			gcTime: time(15, 'm'),
		}),
}

/**
 * Fetch available integrations for current tenant
 */
export function useAvailableIntegrations() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(integrationQueries.available(tenant))
}

const NO_CATALOG_MAP: ReadonlyMap<string, AvailableIntegration> = new Map()

/**
 * The **unfiltered** catalog as Map<id, integration>, without suspending.
 *
 * For chrome that must never wait on the catalog — the Case filter chips render
 * inside a page skeleton, which is itself a `Suspense` fallback. The map is
 * empty until the fetch lands. Unfiltered for the same reason as
 * {@link useAvailableIntegrationsMap}: a hidden definition's id must still
 * resolve to the name its column renders.
 */
export function useAvailableIntegrationsMapSync(): ReadonlyMap<
	string,
	AvailableIntegration
> {
	const tenant = useCurrentTenant()
	const { data } = useQuery({
		...integrationQueries.available(tenant),
		select: selectAvailableAsMap,
	})
	return data ?? NO_CATALOG_MAP
}

/**
 * Fetch available integrations as Map<id, integration>
 */
export function useAvailableIntegrationsMap() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery({
		...integrationQueries.available(tenant),
		select: selectAvailableAsMap,
	})
}

/**
 * Fetch integrations list for current tenant
 */
export function useIntegrationList() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(integrationQueries.list(tenant))
}

/**
 * Fetch integrations with events_enabled as Map<definition_id, connector>
 */
export function useIntegrationListEventsMap() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery({
		...integrationQueries.list(tenant),
		select: selectEventsAsMap,
	})
}

/**
 * Custom hook to fetch the current tenant's integrations details
 * @param integration_id - The ID of the integration
 */
export const useIntegrationDetails = (integration_id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(integrationQueries.detail(tenant, integration_id))
}

/**
 * Custom hook to fetch the current tenant's integrations event types
 * @param integration_id - The ID of the integration
 */
export const useIntegrationEventTypes = (integration_id: string) => {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(integrationQueries.eventTypes(tenant, integration_id))
}

/**
 * Custom hook to fetch the current tenant's integrations definition
 * @param integration_id - The ID of the integration
 */
export const useIntegrationDefinition = (integration_id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(integrationQueries.definition(tenant, integration_id))
}

/**
 * The catalog joined with a connector list, without suspending and without
 * throwing: `undefined` until both load, and while either fails. The catalog is
 * read unfiltered, as {@link useEnrichedIntegrations} reads it.
 */
function useEnrichedRowsQuery(
	list: UseQueryResult<Array<IntegrationConnector>>['data'],
	enabled: boolean,
): Array<EnrichedIntegration> | undefined {
	const tenant = useCurrentTenant()
	const { data: available } = useQuery({
		...integrationQueries.available(tenant),
		select: undefined,
		enabled: enabled && !!tenant,
		throwOnError: false,
	})
	return useMemo(
		() => (available && list ? enrichIntegrations(available, list) : undefined),
		[available, list],
	)
}

/**
 * {@link useEnrichedIntegrations}' rows, without suspending and without
 * throwing: `undefined` until both lists load, and while either fails. Same
 * query keys and the same unfiltered catalog, so the two read one cache.
 */
export function useEnrichedIntegrationsQuery({
	enabled,
}: {
	enabled: boolean
}): Array<EnrichedIntegration> | undefined {
	const tenant = useCurrentTenant()
	const { data: list } = useQuery({
		...integrationQueries.list(tenant),
		enabled,
		throwOnError: false,
	})
	return useEnrichedRowsQuery(list, enabled)
}

/**
 * The connectors of `tenants`, enriched like {@link useEnrichedIntegrations}:
 * joined against the current tenant's unfiltered catalog (the catalog is
 * product copy, the same for every tenant). Suspends; throws when either
 * request fails.
 */
export function useTenantsEnrichedIntegrations(
	tenants: ReadonlyArray<string>,
): Array<EnrichedIntegration> {
	const tenant = useCurrentTenant()
	const { data: available } = useSuspenseQuery({
		...integrationQueries.available(tenant),
		select: undefined,
	})
	const { data: list } = useSuspenseQuery(
		integrationQueries.tenantsList(tenants),
	)
	return useMemo(() => enrichIntegrations(available, list), [available, list])
}

/**
 * {@link useTenantsEnrichedIntegrations}' rows, without suspending and without
 * throwing: `undefined` until both lists load, and while either fails.
 */
export function useTenantsEnrichedIntegrationsQuery(
	tenants: ReadonlyArray<string>,
	{ enabled }: { enabled: boolean },
): Array<EnrichedIntegration> | undefined {
	const { data: list } = useQuery({
		...integrationQueries.tenantsList(tenants),
		enabled: enabled && tenants.length > 0,
		throwOnError: false,
	})
	return useEnrichedRowsQuery(list, enabled)
}

/**
 * Derived enrichment of the tenant's connected integrations: the `available`
 * catalog joined against the tenant `list`, keyed on `definition_id`. The join
 * is an in-memory pass over a handful of rows — a plain `useMemo`, no worker.
 *
 * The catalog is read with its `visible` `select` BYPASSED (`select: undefined`
 * over the same query cache). `visible` is an add-catalog flag, not a
 * connected-list filter — a connector on a `visible:false` definition (e.g.
 * "Liongard Test") must still render in the connected list.
 *
 * Re-exposes `refetch` (both queries), `isFetching` (either), and
 * `dataUpdatedAt` (max) so the list's "last updated / refresh" control works.
 */
export const useEnrichedIntegrations = () => {
	const tenant = useCurrentTenant()

	const availableQuery = useSuspenseQuery({
		...integrationQueries.available(tenant),
		select: undefined,
	})
	const listQuery = useSuspenseQuery(integrationQueries.list(tenant))

	const data = useMemo(
		() => enrichIntegrations(availableQuery.data, listQuery.data),
		[availableQuery.data, listQuery.data],
	)

	const { refetch: refetchAvailable } = availableQuery
	const { refetch: refetchList } = listQuery
	const refetch = useCallback(async () => {
		await Promise.all([refetchAvailable(), refetchList()])
	}, [refetchAvailable, refetchList])

	return {
		data,
		refetch,
		isFetching: availableQuery.isFetching || listQuery.isFetching,
		dataUpdatedAt: Math.max(
			availableQuery.dataUpdatedAt,
			listQuery.dataUpdatedAt,
		),
	}
}

/**
 * Custom hook to fetch the current tenant's Microsoft consent URL
 */
export const useMicrosoftConsentURL = () => {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(integrationQueries.microsoftConsentURL(tenant))
}
